import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Helmet } from 'react-helmet';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const SeriesCalculator = () => {
  const [inputData, setInputData] = useState({
    firstTerm: '',
    commonDifference: '',
    numberOfTerms: '',
  });

  const [results, setResults] = useState(null);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setInputData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const calculateSum = () => {
    const { firstTerm, commonDifference, numberOfTerms } = inputData;

    // Validate input
    if (!firstTerm || !commonDifference || !numberOfTerms) {
      setError('Please enter all required fields');
      return;
    }

    // Convert input to numbers
    const firstTermNum = parseFloat(firstTerm);
    const commonDifferenceNum = parseFloat(commonDifference);
    const numberOfTermsNum = parseInt(numberOfTerms, 10);

    if (isNaN(firstTermNum) || isNaN(commonDifferenceNum) || isNaN(numberOfTermsNum)) {
      setError('Please enter valid numbers for first term, common difference, and number of terms');
      return;
    }

    if (numberOfTermsNum <= 0) {
      setError('Number of terms must be a positive integer');
      return;
    }

    // Calculate the sum of the series
    const lastTerm = firstTermNum + (numberOfTermsNum - 1) * commonDifferenceNum;
    const sum = (numberOfTermsNum / 2) * (firstTermNum + lastTerm);

    // Generate series data for visualization
    const seriesData = [];
    let currentTerm = firstTermNum;
    for (let i = 0; i < numberOfTermsNum; i++) {
      seriesData.push(currentTerm);
      currentTerm += commonDifferenceNum;
    }

    // Create visualization data
    const chartData = {
      labels: seriesData.map((_, i) => `Term ${i + 1}`),
      datasets: [{
        label: 'Series Terms',
        data: seriesData,
        backgroundColor: 'rgba(255, 99, 132, 0.5)',
        borderColor: 'rgba(255, 99, 132, 1)',
        borderWidth: 1
      }]
    };

    // Create calculation steps
    const calculationSteps = [
      `Step 1: Identify the first term (a₁) = ${firstTermNum}`,
      `Step 2: Identify the common difference (d) = ${commonDifferenceNum}`,
      `Step 3: Identify the number of terms (n) = ${numberOfTermsNum}`,
      `Step 4: Calculate the last term (aₙ) = a₁ + (n - 1)d = ${lastTerm}`,
      `Step 5: Use the arithmetic series sum formula: S = n/2 × (a₁ + aₙ)`,
      `Step 6: Calculate the sum: S = ${numberOfTermsNum}/2 × (${firstTermNum} + ${lastTerm}) = ${sum}`
    ];

    setResults({
      firstTerm: firstTermNum,
      commonDifference: commonDifferenceNum,
      numberOfTerms: numberOfTermsNum,
      lastTerm,
      sum,
      calculationSteps,
      chartData
    });
  };

  const exampleData = {
    firstTerm: 1,
    commonDifference: 2,
    numberOfTerms: 10,
    sum: 100
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.5 }}
      className="min-h-screen bg-gradient-to-r from-purple-100 to-pink-100"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Helmet>
          <title>Series Calculator | Arithmetic Series Sum Tool</title>
          <meta name="description" content="Calculate the sum of the first n terms of an arithmetic series." />
          <meta name="keywords" content="series calculator, arithmetic series, sum of series, sequence" />
        </Helmet>

        <div className="relative overflow-hidden bg-white rounded-lg shadow-2xl p-8 mb-8">
          <div className="calculator-header flex items-center mb-8">
            <div className="header-icon text-4xl text-pink-600 mr-4">Σ</div>
            <h1 className="text-3xl font-bold text-gray-800">Series Calculator</h1>
          </div>

          <div className="text-gray-600 mb-8">
            <p>Calculate the sum of the first n terms of an arithmetic series. This calculator helps determine the sum using the arithmetic series formula.</p>
          </div>

          <div className="grid grid-cols-1 gap-6 mb-6">
            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">First Term (a₁):</label>
              <input
                type="number"
                name="firstTerm"
                value={inputData.firstTerm}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                placeholder="Enter first term (e.g., 1)"
              />
            </div>
            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Common Difference (d):</label>
              <input
                type="number"
                name="commonDifference"
                value={inputData.commonDifference}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                placeholder="Enter common difference (e.g., 2)"
              />
            </div>
            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Number of Terms (n):</label>
              <input
                type="number"
                name="numberOfTerms"
                value={inputData.numberOfTerms}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                placeholder="Enter number of terms (e.g., 10)"
              />
            </div>
          </div>

          <button
            onClick={calculateSum}
            className="w-full py-3 px-4 bg-pink-600 text-white font-medium rounded-lg hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2 transition-colors duration-200"
          >
            Calculate Sum
          </button>

          {error && (
            <div className="mt-4 p-4 text-red-700 bg-red-100 rounded-lg">
              {error}
            </div>
          )}

          {results && (
            <div className="mt-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Results</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Series Summary</h3>
                  <p className="text-lg">First Term: {results.firstTerm}</p>
                  <p className="text-lg">Common Difference: {results.commonDifference}</p>
                  <p className="text-lg">Number of Terms: {results.numberOfTerms}</p>
                  <p className="text-lg">Last Term: {results.lastTerm}</p>
                  <p className="text-2xl font-bold text-pink-600 mt-4">Sum: {results.sum}</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Series Visualization</h3>
                  <div className="h-64 bg-white rounded-lg">
                    <Bar
                      data={results.chartData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        scales: {
                          y: {
                            beginAtZero: true,
                            title: {
                              display: true,
                              text: 'Term Value'
                            }
                          },
                          x: {
                            title: {
                              display: true,
                              text: 'Term Position'
                            }
                          }
                        },
                        plugins: {
                          title: {
                            display: true,
                            text: 'Arithmetic Series Terms'
                          }
                        }
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-700 mb-4">Calculation Steps</h3>
                <div className="space-y-4">
                  {results.calculationSteps.map((step, index) => (
                    <div key={index} className="p-4 bg-white rounded-lg shadow-inner">
                      <pre className="whitespace-pre-wrap font-mono text-gray-700">{step}</pre>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Example Calculation Section */}
        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Example Calculation <span className="text-pink-600">📝</span></h2>
          <div className="text-gray-600">
            <p>Here's an example calculation for an arithmetic series:</p>
            
            <div className="bg-white p-6 rounded-lg shadow-md mt-4">
              <h4 className="mb-4">Sample Data:</h4>
              <ul className="list-disc pl-6 mb-4">
                <li>First Term: {exampleData.firstTerm}</li>
                <li>Common Difference: {exampleData.commonDifference}</li>
                <li>Number of Terms: {exampleData.numberOfTerms}</li>
                <li>Sum: {exampleData.sum}</li>
              </ul>

              <h4 className="mb-4">Calculation Method:</h4>
              <ol className="list-decimal pl-6 mb-4">
                <li>Calculate the last term: 1 + (10-1)×2 = 19</li>
                <li>Use the formula: Sum = n/2 × (a₁ + aₙ)</li>
                <li>Sum = 10/2 × (1 + 19) = 5 × 20 = 100</li>
              </ol>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">How to Use <span className="text-pink-600">📋</span></h2>
          <div className="text-gray-600">
            <p>Follow these steps to use the Series Calculator:</p>
            <ol className="list-decimal pl-6 mt-4">
              <li><strong>Enter First Term:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Input the first term of the arithmetic series</li>
                </ul>
              </li>
              <li><strong>Enter Common Difference:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Input the common difference between consecutive terms</li>
                </ul>
              </li>
              <li><strong>Enter Number of Terms:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Input how many terms you want to sum</li>
                </ul>
              </li>
              <li><strong>Click Calculate:</strong> The calculator will determine:
                <ul className="list-disc pl-6 mt-2">
                  <li>The sum of the series</li>
                  <li>A visual representation of the series</li>
                  <li>Detailed calculation steps</li>
                </ul>
              </li>
            </ol>
            <div className="mt-4 p-4 bg-pink-100 rounded-lg">
              <p><strong>💡 Pro Tips:</strong></p>
              <ul className="list-disc pl-6 mt-2">
                <li>The formula works for any arithmetic series (constant difference)</li>
                <li>For geometric series, use a different calculator</li>
                <li>The sum increases quadratically with the number of terms</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Understanding Arithmetic Series <span className="text-pink-600">💻</span></h2>
          <div className="text-gray-600">
            <p>
              An arithmetic series is the sum of the terms of an arithmetic sequence - a sequence of numbers with a constant difference between consecutive terms. The sum of the first n terms can be calculated using the formula:
            </p>
            <p className="font-bold text-xl mt-4">Sₙ = n/2 × (a₁ + aₙ)</p>
            <p className="mt-2">
              Where:
              <ul className="list-disc pl-6 mt-4">
                <li><strong>Sₙ:</strong> Sum of the first n terms</li>
                <li><strong>n:</strong> Number of terms</li>
                <li><strong>a₁:</strong> First term</li>
                <li><strong>aₙ:</strong> nth term</li>
              </ul>
            </p>
            <p className="mt-4">
              Arithmetic series have numerous applications in:
              <ul className="list-disc pl-6 mt-4">
                <li><strong>Mathematics:</strong> Number theory and algebra</li>
                <li><strong>Physics:</strong> Calculating quantities that change linearly over time</li>
                <li><strong>Finance:</strong> Annuities and loan calculations</li>
                <li><strong>Computer Science:</strong> Algorithm analysis</li>
              </ul>
            </p>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Best Practices <span className="text-pink-600">✨</span></h2>
          <div className="text-gray-600">
            <ul className="list-disc pl-6 mt-4">
              <li><strong>Verify Sequence Type:</strong> Ensure your sequence is arithmetic (constant difference)</li>
              <li><strong>Check for Valid Inputs:</strong> Ensure all values are positive numbers where required</li>
              <li><strong>Understand Limitations:</strong> This formula only works for arithmetic sequences</li>
              <li><strong>Use for Large n:</strong> The formula is especially useful for large numbers of terms</li>
            </ul>
            <p className="italic mt-4">
              Note: For non-arithmetic sequences, different summation methods are required.
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default SeriesCalculator;